/* eslint-disable no-unused-expressions */
/* eslint-disable no-nested-ternary */

import React, { useEffect, useState } from 'react';
import MetaList from 'brastrap/common/meta-list/MetaList';
import { defineMessages, injectIntl } from 'react-intl';
import Badge from 'brastrap/common/badge/Badge';
import Sku from 'brastrap/transactional/sku/Sku';
import Price from 'brastrap/promotional/price/Price';
import FieldSet from 'brastrap/common/fieldset/Fieldset';
import FieldSelect from 'brastrap/common/field-select/FieldSelect';
import Button from 'brastrap/common/button/Button';
import ReturnsDetailSummary from './ReturnsDetailSummary';

/**
 * Select items for return
 *
 * @param {Object} props
 * @returns {String|XML}
 */
const ReturnsItemSelection = (props: Props) => {
  const {
    result,
    orderNo,
    onRequestReturn,
    isLoading,
    requestReturnErrorMsg,
    intl: { formatMessage, formatNumber, formatDate },
  } = props;

  const [returnedItems, setReturnedItems] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [itemsToReturn, setItemsToReturn] = useState([]);
  const [isSmallWindowWidth, setIsSmallWidowWidth] = useState(
    getWindowDimensions().width < 560
  );

  const messages = defineMessages({
    returned: { id: 'returns.returned', defaultMessage: 'Returned' },
    quantitySelectLabel: {
      id: 'returns.select-quantity-label',
      defaultMessage: 'Quantity to return',
    },
    quantitySelectLabelMobile: {
      id: 'returns.select-quantity-label-mobile',
      defaultMessage: 'Quantity',
    },
    scrollToTop: {
      id: 'returns.scroll-to-top-button',
      defaultMessage: 'Back to top',
    },
    itemTotalInclTax: {
      id: 'returns.item-total-incl-tax',
      defaultMessage: 'Item total (incl. tax)',
    }
  });

  const metaTitles = defineMessages({
    orderDate: { id: 'returns.order-date', defaultMessage: 'Order Date' },
    orderNumber: {
      id: 'returns.order-number',
      defaultMessage: 'Order Number',
    },
    total: { id: 'returns.total', defaultMessage: 'Total' },
  });

  const metaItems = [
    {
      name: formatMessage(metaTitles.orderDate),
      value: formatDate(new Date(result.orderedDate), {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    },
    {
      name: formatMessage(metaTitles.orderNumber),
      value: orderNo || 'Pending',
    },
    {
      name: formatMessage(metaTitles.total),
      value: formatNumber(result.totalValue, {
        style: 'currency',
        currency: result.currency,
      }),
    },
  ];

  useEffect(() => {
    const newReturnedItemsArr = [];
    const remainingOrderItems = [];

    result &&
      result.items.forEach(orderItem => {
        orderItem.price = orderItem.value / orderItem.quantity;
        result.returns &&
          result.returns.forEach(returnOrder => {
            returnOrder.items.forEach(returnItem => {
              if (orderItem.orderItemId === returnItem.orderItemId) {
                const remainingItem = { ...orderItem };
                const returnedItem = { ...orderItem };

                //Look into the remaining items array to see if it exists
                if (
                  remainingOrderItems.some(
                    item => item.orderItemId === returnItem.orderItemId
                  )
                ) {
                  //If it does then get it and recalculate the quantity and value
                  const existingItem = remainingOrderItems.find(
                    item => item.orderItemId === returnItem.orderItemId
                  );

                  const newQuantity =
                    existingItem.quantity - returnItem.quantity;

                  if (newQuantity > 0) {
                    existingItem.quantity = newQuantity;
                    existingItem.value = existingItem.price * newQuantity;

                    //replace the existing item with the updated version
                    const index = remainingOrderItems.indexOf(existingItem);
                    remainingOrderItems[index] = existingItem;
                  }
                } else {
                  //go through standard route
                  const newQuantity =
                    remainingItem.quantity - returnItem.quantity;

                  if (newQuantity > 0) {
                    remainingItem.quantity = newQuantity;
                    remainingItem.value = remainingItem.price * newQuantity;

                    remainingOrderItems.push(remainingItem);
                  }
                }

                if (
                  newReturnedItemsArr.some(
                    item => item.orderItemId === returnItem.orderItemId
                  )
                ) {
                  //If it does then get it and recalculate the quantity and value
                  const existingItem = newReturnedItemsArr.find(
                    item => item.orderItemId === returnItem.orderItemId
                  );

                  existingItem.quantity = existingItem.quantity + returnItem.quantity;
                  existingItem.value = existingItem.price * existingItem.quantity;

                  //replace the existing item with the updated version
                  const index = newReturnedItemsArr.indexOf(existingItem);
                  newReturnedItemsArr[index] = existingItem;
                } else {
                  returnedItem.quantity = returnItem.quantity;
                  returnedItem.value = returnedItem.price * returnedItem.quantity;
                  newReturnedItemsArr.push(returnedItem);
                }
              }
            });
          });
      });

    let newOrderItemsArr = result.items;

    newOrderItemsArr = newOrderItemsArr.filter(
      item =>
        !newReturnedItemsArr.some(
          retItem => item.orderItemId === retItem.orderItemId
        )
    );

    setOrderItems(newOrderItemsArr.concat(remainingOrderItems));
    setReturnedItems(newReturnedItemsArr);
  }, [result]);

  useEffect(() => {
    /**
     * Set new window dimensions whenever resize occurs
     */
    function handleResize() {
      if (getWindowDimensions().width < 560) {
        setIsSmallWidowWidth(true);
      } else {
        setIsSmallWidowWidth(false);
      }
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /**
   * Gets the window dimensions for window rezising/device width for specific styling
   * @returns {Object}
   */
  function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;
    return {
      width,
      height,
    };
  }

  /**
   * Builds quantity options
   * @param {Array} quantity
   * @returns {Array}
   */
  function buildQuantityOptions(quantity) {
    const optionsArray = [];
    for (let i = 0; i < quantity; i++) {
      optionsArray.push({ label: i + 1, value: i + 1 });
    }
    return optionsArray;
  }

  /**
   * Removes selected item from bag
   * @param {Object} item
   */
  function removeItemFromReturnsBag(item) {
    const newArr = itemsToReturn.filter(itemToReturn => {
      return itemToReturn.orderItemId !== item.orderItemId;
    });
    setItemsToReturn(newArr);
  }

  /**
   * Gets quantity value from bag
   * @param {Number} orderItemId
   * @returns {Number}
   */
  function getQuantityValue(orderItemId) {
    const foundItems = itemsToReturn.find(
      item => item.orderItemId === orderItemId
    );
    return foundItems ? foundItems.quantity : 0;
  }

  /*
   * Handles on change
   * @param {String} value
   * @param {Object} item
   * @param {String} type
   */
  function handleSelectOnChange(quantity, item) {
    const existingItem = itemsToReturn.find(
      itemToReturn => itemToReturn.orderItemId === item.orderItemId
    );
    if (existingItem) {
      const newArr = [...itemsToReturn];
      const index = newArr.indexOf(existingItem);

      if (quantity === '') {
        removeItemFromReturnsBag(item);
      } else {
        existingItem.value = item.price * quantity;
        existingItem.quantity = quantity;
        newArr[index] = existingItem;

        setItemsToReturn(newArr);
      }
    } else {
      setItemsToReturn(items => [
        ...items,
        {
          orderItemId: item.orderItemId,
          colour: item.colour,
          image: item.image,
          styleColourCode: item.styleColourCode,
          styleName: item.styleName,
          size: item.size,
          value: item.price * quantity,
          colourSwatch: item.swatch,
          brandName: item.brand,
          quantity: quantity,
          price: item.price,
        },
      ]);
    }
  }

  /**
   * Returns highlight background colour depending on what is in the bag
   * @param {Object} currentItem
   * @returns {String}
   */
  function highlightSelectedItem(currentItem) {
    const foundItems = itemsToReturn.find(
      item => item.orderItemId === currentItem.orderItemId
    );
    return foundItems && foundItems.quantity !== 0 ? '#FFF6CC' : '#FFFFFF';
  }

  /**
   * Scrolls to top of page
   */
  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div className="returns-item-selection__container">
      <div className="returns-item-selection__mainContainer">
        <section className="returns-item-selection">
          <footer className="returns-item-selection__footer">
            <MetaList meta={metaItems} />
          </footer>

          <section className="returns-item-selection__main">
            <ul className="returns-item-selection__items">
              <ul id="returned-items">
                {returnedItems.map((item, key) => (
                  <li key={key} className="returns-item-selection__item--row">
                    <div className="returns-item-selection__skuContainer">
                      <Sku
                        item={{
                          styleColourCode: item.styleColourCode,
                          styleCode: item.styleColourCode,
                          styleName: item.styleName,
                          size: { size: item.size },
                          totalPrice: item.value,
                          quantity: item.quantity,
                          colour: item.colour,
                          imgUrl: item.image,
                          colourSwatch: item.swatch,
                          brandName: item.brand,
                        }}
                        showQuantity
                        showBrandName
                      />
                    </div>
                    {formatMessage(messages.itemTotalInclTax)}&nbsp;
                    <div className="returns-item-selection__badgeAndPriceContainer">
                      <Badge
                        label={props.intl.formatMessage(messages.returned)}
                      />
                      <Price
                        id={`returnItem__${key}`}
                        price={item.value}
                        currency={result.currency}
                      />
                    </div>
                  </li>
                ))}
              </ul>

              <ul id="returnable-items">
                {orderItems.map((item, key) => (
                  <li
                    id="item-to-return"
                    style={{ background: highlightSelectedItem(item) }}
                    key={key}
                    className={
                      isSmallWindowWidth
                        ? 'returns-item-selection__item--column'
                        : 'returns-item-selection__item--row'
                    }
                  >
                    <div className="returns-item-selection__skuContainer">
                      <Sku
                        item={{
                          styleColourCode: item.styleColourCode,
                          styleCode: item.styleColourCode,
                          styleName: item.styleName,
                          size: { size: item.size },
                          totalPrice: item.value,
                          quantity: item.quantity,
                          colour: item.colour,
                          imgUrl: item.image,
                          colourSwatch: item.swatch,
                          swatchPatternImage: item.swatchPatternImage,
                          brandName: item.brand,
                        }}
                        showQuantity
                        showBrandName
                      />
                      {isSmallWindowWidth && (
                        <>
                          {formatMessage(messages.itemTotalInclTax)}
                          <Price
                            id={`orderItemMobile__${key}`}
                            price={item.value}
                            currency={result.currency}
                          />
                        </>
                      )}
                    </div>

                    <div className="returns-item-selection__fieldContainer">
                      {!isSmallWindowWidth && (
                        <div className="returns-item-selection__tax-price">
                          {formatMessage(messages.itemTotalInclTax)}&nbsp;
                          <Price
                            id={`orderItemDesktop__${key}`}
                            price={item.value}
                            currency={result.currency}
                          />
                        </div>
                      )}
                      <FieldSet>
                        <FieldSelect
                          onChange={ev => {
                            handleSelectOnChange(ev.target.value, item);
                          }}
                          label={
                            !isSmallWindowWidth
                              ? formatMessage(messages.quantitySelectLabel)
                              : formatMessage(
                                messages.quantitySelectLabelMobile
                              )
                          }
                          placeholder={'0'}
                          options={buildQuantityOptions(item.quantity)}
                          required
                          value={getQuantityValue(item.orderItemId)}
                          id="quantityToReturn"
                        />
                      </FieldSet>
                    </div>
                  </li>
                ))}
              </ul>
            </ul>
          </section>
        </section>

        {isSmallWindowWidth && result && result.items.length > 0 && (
          <Button
            id="scrollToTop"
            label={formatMessage(messages.scrollToTop)}
            modifiers={['full-width-mobile']}
            onClick={() => {
              scrollToTop();
            }}
          />
        )}
      </div>

      <section className="returns-item-select__basket">
        <ReturnsDetailSummary
          isSmallWindowWidth={isSmallWindowWidth}
          onRequestReturn={(zipcode, orderNumber) => {
            scrollToTop();
            onRequestReturn(zipcode, orderNumber);
          }}
          result={result}
          isLoading={isLoading}
          itemsToReturn={itemsToReturn}
          setItemsToReturn={setItemsToReturn}
          requestReturnErrorMsg={requestReturnErrorMsg}
        />
      </section>
    </div>
  );
};

ReturnsItemSelection.propTypes = {};

ReturnsItemSelection.defaultProps = {};

export default injectIntl(ReturnsItemSelection);
